import React from 'react';
import classNames from 'classnames';
import { CSSProp } from 'styled-components';

type ColorType =
  | 'primary'
  | 'turf-green'
  | 'slate-green'
  | 'black'
  | 'blue'
  | 'secondary'
  | 'warning'
  | 'danger'
  | 'darkgrey'
  | 'grey';
type CustomButtonProps = {
  color?: ColorType;
  fluid?: boolean;
  size?: 'tiny' | 'small' | 'normal' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  ref?: React.Ref<HTMLButtonElement>;
};

export type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'color'
> &
  CustomButtonProps & {
    enabled?: boolean;
    as?: string | React.ComponentType;
    css?: CSSProp;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as,
      children,
      className,
      color = 'primary',
      disabled,
      enabled,
      fluid,
      size = 'normal',
      type = 'button',
      variant = 'contained',
      ...props
    },
    ref,
  ) => {
    const disabledCx =
      'disabled:cursor-not-allowed disabled:text-pastel-green-500 disabled:bg-off-white-500 disabled:border disabled:border-grey-500';
    const variantCx =
      variant === 'contained'
        ? [
            'border hover:shadow focus:ring',
            disabledCx,
            {
              'bg-turf-green-500 border-turf-green-500 text-white hover:bg-turf-green-900 hover:border-turf-green-900':
                color === 'primary' || color === 'turf-green',
              'bg-slate-green-500 border-slate-green-500 text-white hover:bg-slate-green-600 hover:border-slate-green-600':
                color === 'slate-green',
              'bg-blue-500 border-blue-500 hover:bg-blue-600 hover:border-blue-600 text-white':
                color === 'blue',
              'bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600 text-white':
                color === 'danger',
              'bg-yellow-500 border-yellow-500 hover:bg-yellow-600 hover:border-yellow-600 text-white':
                color === 'warning',
              'bg-darkgrey-500 border-darkgrey-500 hover:bg-darkgrey-600 hover:border-darkgrey-600 text-white':
                color === 'darkgrey' || color === 'secondary',
              'bg-white hover:bg-grey-100 text-turf-green-500':
                color === 'grey' || color === 'black',
            },
          ]
        : variant === 'outlined'
        ? [
            'border focus:ring',
            disabledCx,
            {
              'border-primary-500 hover:bg-primary-100 text-primary-500':
                color === 'primary',
              'border-blue-500 hover:bg-blue-100 text-blue-500':
                color === 'blue',
              'border-red-500 hover:bg-red-100 text-red-500':
                color === 'danger',
              'border-yellow-500 hover:bg-yellow-100 text-yellow-500':
                color === 'warning',
              'border-darkgrey-500 hover:bg-darkgrey-100 text-darkgrey-500':
                color === 'darkgrey' || color === 'secondary',
              'hover:bg-grey-100 text-turf-green-500':
                color === 'grey' || color === 'black' || !color,
            },
          ]
        : variant === 'text'
        ? [
            'text-turf-green-500 focus:outline-none disabled:text-pastel-green-500 focus:ring',
          ]
        : null;

    const Element = as || 'button';

    return (
      <Element
        className={classNames(
          'rounded font-semibold transition duration-300 appearance-none',
          {
            'px-2 py-1 text-xs': size === 'tiny',
            'px-4 py-1.5 text-sm': size === 'small',
            'px-6 py-2 text-sm': size === 'normal',
            'px-6 py-3 text-sm': size === 'large',
            'w-full flex-1': fluid,
          },
          variantCx,
          // variant === 'text' && 'border-0',
          className,
        )}
        type={type}
        disabled={disabled || (enabled == null ? false : !enabled)}
        {...props}
        // @ts-ignore
        ref={ref}
      >
        {children}
      </Element>
    );
  },
);

type ButtonLinkProps = Omit<ButtonProps, 'as'> & {
  as: React.ReactElement;

  /**
   * Currently, for some reason, React.cloneElement and Styled Components `css` prop
   * do not get along. This causes some weird internal React error, so don't do it until
   * it may be fixed or better supported.
   */
  css?: never;
};

export const ButtonLink: React.FC<ButtonLinkProps> = ({
  as,
  children,
  disabled,
  enabled,
  ...props
}) => {
  const realDisabled = disabled || (enabled == null ? false : !enabled);

  const button = (
    <Button {...props} disabled={realDisabled} enabled={enabled}>
      {children}
    </Button>
  );

  if (realDisabled) {
    return button;
  } else {
    return React.cloneElement(React.Children.only(as), {}, button);
  }
};
